import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import LoadingSpinner from './LoadingSpinner';
import VivendiusLogo from './VivendiusLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractSignature = () => {
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState(null);
  const [currentBuyer, setCurrentBuyer] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  
  const signatureRef = useRef();

  useEffect(() => {
    loadContract();
  }, [contractId]);

  const loadContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}`);
      setContract(response.data);
      
      // Get buyer email and DNI from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const buyerEmail = urlParams.get('email');
      const buyerDni = urlParams.get('dni');
      
      if (response.data.signers && response.data.signers.length > 0) {
        let foundBuyer = null;
        
        if (buyerEmail && buyerDni) {
          // Match by BOTH email AND DNI for security
          foundBuyer = response.data.signers.find(b => 
            b.email && b.email.toLowerCase() === buyerEmail.toLowerCase() &&
            b.dni && b.dni.toUpperCase() === buyerDni.toUpperCase()
          );
        } else if (buyerEmail) {
          // Fallback: find by email only, but must be unique
          const matchingSigners = response.data.signers.filter(b => 
            b.email && b.email.toLowerCase() === buyerEmail.toLowerCase()
          );
          // Only auto-select if there's exactly one match
          if (matchingSigners.length === 1) {
            foundBuyer = matchingSigners[0];
          } else if (matchingSigners.length > 1) {
            // Multiple signers with same email - require DNI
            setError('Hay varios firmantes con el mismo email. Por favor, use el enlace específico enviado a su correo.');
            setLoading(false);
            return;
          }
        }
        // Removed: no longer auto-select first unsigned buyer if no email provided
        
        if (foundBuyer) {
          setCurrentBuyer(foundBuyer);
          
          // Check if this buyer has already signed
          if (foundBuyer.signed_at) {
            setSigned(true);
          }
        } else if (buyerEmail) {
          setError('No se encontró información del comprador para este enlace. Por favor, use el enlace específico enviado a su correo.');
        } else {
          setError('Enlace de firma inválido. Por favor, use el enlace enviado a su correo electrónico.');
        }
      }
      
      // Check if contract is fully signed
      if (response.data.status === 'signed') {
        setSigned(true);
      }
    } catch (err) {
      console.error('Error loading contract:', err);
      setError('Error al cargar el contrato: ' + (err.response?.data?.detail || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    signatureRef.current.clear();
    setSignatureData(null);
  };

  const saveSignature = () => {
    if (!signatureRef.current.isEmpty()) {
      const canvas = signatureRef.current.getCanvas();
      const signatureDataURL = canvas.toDataURL('image/png');
      setSignatureData(signatureDataURL);
      setShowSignaturePad(false);
    } else {
      alert('Por favor, añada su firma antes de continuar.');
    }
  };

  const handleSubmitSignature = async (e) => {
    e.preventDefault();
    
    if (!signatureData) {
      alert('Por favor, añada su firma manuscrita antes de continuar.');
      return;
    }
    
    setSigning(true);
    setError(null);
    
    try {
      const submitData = {
        contract_id: contractId,
        signer_name: currentBuyer.name,
        signer_email: currentBuyer.email,
        signer_dni: currentBuyer.dni,
        signature_image: signatureData
      };
      
      const response = await axios.post(`${API}/contracts/${contractId}/buyer-sign`, submitData);
      
      if (response.data.all_signed) {
        setSigned(true);
        alert('¡Contrato firmado exitosamente! Todos los compradores han completado la firma.');
      } else {
        setSigned(true);
        alert('Su firma ha sido registrada exitosamente. Esperando firmas de otros compradores.');
      }
      
      // Refresh contract data
      await loadContract();
    } catch (err) {
      console.error('Error signing contract:', err);
      setError('Error al firmar el contrato: ' + (err.response?.data?.detail || 'Error desconocido'));
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadAttachment = async (attachmentIndex, filename) => {
    setDownloadingAttachment(attachmentIndex);
    
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/attachments/${attachmentIndex}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setError('Error al descargar el documento');
    } finally {
      setDownloadingAttachment(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadPDF = async () => {
    setDownloadingPdf(true);
    
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Error al descargar el PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <VivendiusLogo size="medium" />
            </div>
            <div className="text-sm text-gray-500">
              Sistema de Firma Digital
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-yellow-500">
            <h2 className="text-2xl font-bold text-white">Firma de Contrato</h2>
            <p className="text-purple-100 mt-1">
              Contrato ID: {contractId?.substr(0, 8)}...
            </p>
          </div>

          <div className="p-6">
            {signed ? (
              <div className="text-center py-8">
                <div className="text-green-500 text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Contrato Firmado Exitosamente
                </h3>
                <p className="text-gray-600 mb-6">
                  El contrato ha sido firmado digitalmente. Puedes descargar una copia del contrato firmado.
                </p>
                
                <div className="flex flex-col space-y-4 max-w-md mx-auto">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={downloadingPdf}
                    className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {downloadingPdf ? (
                      <>
                        <LoadingSpinner size="small" text="" color="text-white" />
                        <span>Descargando PDF...</span>
                      </>
                    ) : (
                      <span>Descargar Contrato PDF</span>
                    )}
                  </button>
                  
                  {/* Show attachments if available */}
                  {contract?.attachments && contract.attachments.length > 0 && (
                    <div className="text-left">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Documentos Adjuntos ({contract.attachments.length})
                      </h4>
                      <div className="space-y-2">
                        {contract.attachments.map((attachment, index) => (
                          <button
                            key={index}
                            onClick={() => handleDownloadAttachment(index, attachment.filename)}
                            disabled={downloadingAttachment === index}
                            className="w-full text-left px-3 py-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {downloadingAttachment === index ? (
                              <>
                                <LoadingSpinner size="small" text="" color="text-gray-600" />
                                <span>⏳ {attachment.filename}</span>
                              </>
                            ) : (
                              <span>📎 {attachment.filename}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Información del Contrato
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Comprador:</p>
                        <p className="font-medium">{currentBuyer?.name || 'No disponible'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email:</p>
                        <p className="font-medium">{currentBuyer?.email || 'No disponible'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">DNI/NIE:</p>
                        <p className="font-medium">{currentBuyer?.dni || 'No disponible'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Estado:</p>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pendiente de Firma
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Vista Previa del Contrato
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={downloadingPdf}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {downloadingPdf ? (
                        <>
                          <LoadingSpinner size="small" text="" color="text-white" />
                          <span>Descargando PDF...</span>
                        </>
                      ) : (
                        <span>Ver/Descargar Contrato PDF</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Attachments section */}
                {contract?.attachments && contract.attachments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Documentos Adjuntos ({contract.attachments.length})
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {contract.attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                {attachment.content_type.startsWith('image/') ? (
                                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                                <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDownloadAttachment(index, attachment.filename)}
                              disabled={downloadingAttachment === index}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            >
                              {downloadingAttachment === index ? (
                                <>
                                  <LoadingSpinner size="small" text="" color="text-white" />
                                  <span>Descargando...</span>
                                </>
                              ) : (
                                <span>Descargar</span>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmitSignature} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Firmar Contrato
                  </h3>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buyer Information Display */}
                  {currentBuyer && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Información del Firmante</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Nombre Completo</p>
                          <p className="font-medium">{currentBuyer.name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-medium">{currentBuyer.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">DNI/NIE</p>
                          <p className="font-medium">{currentBuyer.dni}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Signature Section */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Firma Manuscrita *</h4>
                    
                    {!showSignaturePad && !signatureData && (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-gray-500 mb-4">Haga clic para añadir su firma manuscrita</p>
                        <button
                          type="button"
                          onClick={() => setShowSignaturePad(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Añadir Firma
                        </button>
                      </div>
                    )}

                    {showSignaturePad && (
                      <div className="border border-gray-300 rounded-lg p-4">
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">Firme en el área de abajo:</p>
                          <div className="border border-gray-400 rounded">
                            <SignatureCanvas
                              ref={signatureRef}
                              canvasProps={{
                                width: 400,
                                height: 200,
                                className: 'signature-canvas'
                              }}
                              backgroundColor="white"
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={clearSignature}
                            className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
                          >
                            Limpiar
                          </button>
                          <button
                            type="button"
                            onClick={saveSignature}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                          >
                            Guardar Firma
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowSignaturePad(false)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {signatureData && !showSignaturePad && (
                      <div className="border border-gray-300 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-2">Su firma:</p>
                        <div className="border border-gray-200 rounded bg-white p-2 mb-2">
                          <img src={signatureData} alt="Firma" className="max-w-full h-auto" />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSignatureData(null);
                            setShowSignaturePad(true);
                          }}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Cambiar Firma
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Información sobre la Firma Digital:</strong><br />
                          Al firmar este contrato, se registrará su dirección IP, navegador web y timestamp para propósitos de verificación legal.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4">
                    <button
                      type="submit"
                      disabled={signing || !signatureData}
                      className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {signing ? (
                        <>
                          <LoadingSpinner size="small" text="" color="text-white" />
                          <span>Firmando contrato...</span>
                        </>
                      ) : (
                        <span>Firmar Contrato</span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractSignature;